import React, { useEffect, useMemo, useState } from "react";
import type { Task } from "@meupedaco/shared";
import { Link } from "react-router-dom";
import { PageState } from "../components/PageState";
import { useAuth } from "../auth/AuthContext";
import { TASK_STATUS_LABEL } from "../lib/i18n/tasks";

type CalendarEvent = {
  id: number;
  date: string;
  name: string;
  kind: string;
  scope: string;
};

type AgendaResponse = {
  calendar: CalendarEvent[];
  tasks: { data: Task[]; total: number };
};

export const HomePage: React.FC = () => {
  const { client } = useAuth();
  const today = useMemo(() => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  }, []);
  const defaultTo = useMemo(() => {
    const now = new Date();
    now.setDate(now.getDate() + 14);
    return now.toISOString().slice(0, 10);
  }, []);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(defaultTo);
  const [agenda, setAgenda] = useState<AgendaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return params.toString();
  }, [from, to]);

  useEffect(() => {
    const fetchAgenda = async () => {
      setLoading(true);
      setError(null);
      const response = await client.get(`/home/agenda?${query}`);
      if (!response.ok) {
        setError("Falha ao carregar agenda.");
        setAgenda(null);
        setLoading(false);
        return;
      }
      setAgenda((await response.json()) as AgendaResponse);
      setLoading(false);
    };
    fetchAgenda();
  }, [client, query]);

  const groupedDates = useMemo(() => {
    const start = from ? new Date(from) : new Date();
    const end = to ? new Date(to) : start;
    const dates: string[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      dates.push(cursor.toISOString().slice(0, 10));
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }, [from, to]);

  const taskByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    (agenda?.tasks.data ?? []).forEach((task) => {
      const date = task.dueDate ? task.dueDate.slice(0, 10) : "Sem data";
      const list = map.get(date) ?? [];
      list.push(task);
      map.set(date, list);
    });
    return map;
  }, [agenda]);

  const calendarByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    (agenda?.calendar ?? []).forEach((event) => {
      const date = event.date.slice(0, 10);
      const list = map.get(date) ?? [];
      list.push(event);
      map.set(date, list);
    });
    return map;
  }, [agenda]);

  const taskBadgeProps = (status?: string | null) => {
    switch (status) {
      case "OPEN":
        return { className: "badge text-bg-warning", style: undefined };
      case "IN_PROGRESS":
        return { className: "badge text-bg-primary", style: { backgroundColor: "#6f42c1" } };
      case "DONE":
        return { className: "badge text-bg-success", style: undefined };
      default:
        return { className: "badge text-bg-secondary", style: undefined };
    }
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">Agenda</h1>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">De</label>
              <input
                className="form-control"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ate</label>
              <input
                className="form-control"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      <PageState loading={loading} error={error}>
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
              <div>
                <h5 className="mb-1">Agenda do periodo</h5>
                <small className="text-muted">
                  {from} ate {to}
                </small>
              </div>
              <div className="d-flex gap-2">
                <Link to="/governanca/tarefas" className="btn btn-sm btn-outline-primary">
                  Ver tarefas
                </Link>
                <Link to="/governanca/calendario" className="btn btn-sm btn-outline-primary">
                  Gerenciar calendario
                </Link>
              </div>
            </div>
            {groupedDates.map((date) => {
              const tasks = taskByDate.get(date) ?? [];
              const events = calendarByDate.get(date) ?? [];
              if (tasks.length === 0 && events.length === 0) {
                return null;
              }
              return (
                <div key={date} className="border-top pt-3">
                  <div className="fw-semibold mb-2">{date}</div>
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <div className="card border-0 bg-light-subtle h-100">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Tarefas</strong>
                            <span className="text-muted small">{tasks.length}</span>
                          </div>
                          {tasks.length === 0 ? (
                            <div className="text-muted small">Sem tarefas.</div>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              {tasks.map((task) => (
                                <div
                                  key={task.id}
                                  className="d-flex justify-content-between align-items-center"
                                >
                                  <div>
                                    <div className="fw-semibold">{task.title}</div>
                                    <small className="text-muted">
                                      {task.dueDate ? task.dueDate.slice(0, 16).replace("T", " ") : "Sem vencimento"}
                                    </small>
                                  </div>
                                  <span {...taskBadgeProps(task.status)}>
                                    {task.status
                                      ? TASK_STATUS_LABEL[task.status] ?? task.status
                                      : "-"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="card border-0 bg-light-subtle h-100">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Calendario</strong>
                            <span className="text-muted small">{events.length}</span>
                          </div>
                          {events.length === 0 ? (
                            <div className="text-muted small">Sem eventos.</div>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              {events.map((event) => (
                                <div
                                  key={event.id}
                                  className="d-flex justify-content-between align-items-center"
                                >
                                  <div className="fw-semibold">{event.name}</div>
                                  <span
                                    className={`badge ${
                                      event.kind === "HOLIDAY" ? "text-bg-danger" : "text-bg-info"
                                    }`}
                                  >
                                    {event.kind === "HOLIDAY" ? "Feriado" : "Comemoracao"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {groupedDates.every(
              (date) =>
                (taskByDate.get(date) ?? []).length === 0 &&
                (calendarByDate.get(date) ?? []).length === 0
            ) && <div className="text-muted">Sem eventos ou tarefas no periodo.</div>}
          </div>
        </div>
      </PageState>
    </div>
  );
};
